import type { BoardLayoutItem, BoardWidget } from '@amzn/ad-console-canvas';
import { MAX_CHARTS_PER_ROW, MAX_KPIS_PER_ROW, WIDGET_DIMENSIONS, WidgetCategory, getWidgetDimensions } from './constants';

const widgetInstanceId = (w: BoardWidget): string => w.widgetId ?? w.id ?? w.type ?? '';

/**
 * Computes the canonical canvas layout for a set of widgets.
 *
 * Layout is fully derived from `widgets`. Widgets are zoned by category in this order:
 *
 *   1. Summary widgets (full-width rows)
 *   2. KPI widgets (`MAX_KPIS_PER_ROW` per row)
 *   3. Chart widgets (`MAX_CHARTS_PER_ROW` per row)
 *
 * Within a zone, widgets keep the order they appear in the input array. So if the user
 * has two KPI rows partially filled and then adds another KPI, the new KPI appends to
 * the end of the KPI zone (filling the last partial row first), and any chart widgets
 * shift down automatically.
 *
 * Pure — does not mutate the input.
 */

const PER_ROW_LIMIT: Partial<Record<WidgetCategory, number>> = {
  [WidgetCategory.SUMMARY]: 1,
  [WidgetCategory.KPI]: MAX_KPIS_PER_ROW,
  [WidgetCategory.CHART]: MAX_CHARTS_PER_ROW
};

const ZONE_ORDER: readonly WidgetCategory[] = [WidgetCategory.SUMMARY, WidgetCategory.KPI, WidgetCategory.CHART];

/** Returns true if a widget is a table (always rendered full-width in the chart zone). */
const isTableWidget = (widget: BoardWidget): boolean => (widget.type ?? '').includes('canvas-widget-table');

/** Reads the category every canvas widget is required to declare in `props.category`. */
const categoryOf = (widget: BoardWidget): WidgetCategory => {
  const category = (widget.props as { category?: WidgetCategory }).category;
  if (!category) {
    throw new Error(
      `Canvas widget "${widgetInstanceId(widget)}" is missing props.category — every widget on the canvas must declare its category.`
    );
  }
  return category;
};

/** Returns layout dimensions for a widget, accounting for tables in the chart zone. */
const dimensionsOf = (widget: BoardWidget, zoneCategory: WidgetCategory): { w: number; h: number; flexHeight: boolean } =>
  isTableWidget(widget) ? WIDGET_DIMENSIONS.TABLE : getWidgetDimensions(zoneCategory);

/** Lays out a homogeneous set of widgets starting at `startY`. Returns the items and the y-coordinate just below the zone. */
const packZone = (
  widgets: BoardWidget[],
  category: WidgetCategory,
  startY: number,
  expandedIds?: ReadonlySet<string>
): { items: BoardLayoutItem[]; nextY: number } => {
  if (widgets.length === 0) return { items: [], nextY: startY };

  const { w: defaultW, h: defaultH } = getWidgetDimensions(category);
  const perRow = PER_ROW_LIMIT[category] ?? 1;

  // Chart zone: pack row by row, respecting expanded widgets and table widgets (always full-width)
  if (category === WidgetCategory.CHART) {
    const items: BoardLayoutItem[] = [];
    let cursorX = 0;
    let cursorY = startY;
    for (const widget of widgets) {
      const id = widgetInstanceId(widget);
      const dims = dimensionsOf(widget, category);
      const w = dims.w === 12 || expandedIds?.has(id) ? 12 : defaultW;
      const h = dims.h;
      if (cursorX + w > 12) {
        cursorX = 0;
        cursorY += defaultH;
      }
      items.push({ id, x: cursorX, y: cursorY, w, h, ...(dims.flexHeight && { flexHeight: true }) });
      cursorX += w;
      if (cursorX >= 12) {
        cursorX = 0;
        cursorY += h;
      }
    }
    return { items, nextY: cursorX > 0 ? cursorY + defaultH : cursorY };
  }

  // Non-chart zones: simple grid packing
  const dims = getWidgetDimensions(category);
  const items: BoardLayoutItem[] = widgets.map((widget, index) => ({
    id: widgetInstanceId(widget),
    x: (index % perRow) * defaultW,
    y: startY + Math.floor(index / perRow) * defaultH,
    w: defaultW,
    h: defaultH,
    ...(dims.flexHeight && { flexHeight: true })
  }));
  const rowsUsed = Math.ceil(widgets.length / perRow);
  return { items, nextY: startY + rowsUsed * defaultH };
};

/** Generates a stacked full-width layout for a given column count, zoned by category. */
export const recomputeFullWidthLayout = (widgets: BoardWidget[], cols: number): BoardLayoutItem[] => {
  const byCategory: Partial<Record<WidgetCategory, BoardWidget[]>> = {
    [WidgetCategory.SUMMARY]: [],
    [WidgetCategory.KPI]: [],
    [WidgetCategory.CHART]: []
  };
  for (const widget of widgets) {
    const cat = categoryOf(widget);
    (byCategory[cat] ??= []).push(widget);
  }

  const items: BoardLayoutItem[] = [];
  let cursorY = 0;
  for (const category of ZONE_ORDER) {
    for (const widget of byCategory[category] ?? []) {
      const dims = dimensionsOf(widget, category);
      items.push({
        id: widgetInstanceId(widget),
        x: 0,
        y: cursorY,
        w: cols,
        h: dims.h,
        ...(dims.flexHeight && { flexHeight: true })
      });
      cursorY += dims.h;
    }
  }
  return items;
};

export const recomputeLayout = (widgets: BoardWidget[], expandedIds?: ReadonlySet<string>): BoardLayoutItem[] => {
  const byCategory: Partial<Record<WidgetCategory, BoardWidget[]>> = {
    [WidgetCategory.SUMMARY]: [],
    [WidgetCategory.KPI]: [],
    [WidgetCategory.CHART]: []
  };
  for (const widget of widgets) {
    const cat = categoryOf(widget);
    (byCategory[cat] ??= []).push(widget);
  }

  const layout: BoardLayoutItem[] = [];
  let cursorY = 0;
  for (const category of ZONE_ORDER) {
    const { items, nextY } = packZone(byCategory[category] ?? [], category, cursorY, expandedIds);
    layout.push(...items);
    cursorY = nextY;
  }
  return layout;
};
